import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useViewport } from "hooks/useViewport";
import { Dialog } from "components";
import { SparklesIcon, CloseIcon } from "./shared/icons";
import { useDialogStyles } from "./style";
import { NewLearning } from "./steps/new-learning/new-learning";
import { LearningAnalysis } from "./steps/learning-analysis/learning-analysis";
import { useTranslation } from "react-i18next";
import { NewLearningRequestStep, WorkflowStageEnum } from "utils/types";
import { CourseDetails } from "./steps/new-learning-details/course-details/course-details";
import { BudgetCheck } from "./steps/new-learning-details/budget-check/budget-check";
import { useCancelLearningRequest } from "hooks/useCancelLearningRequest";
import { ProductRecommendations } from "./steps/product-recommendations/product-recommendations";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "store";
import { setWorkflowStage } from "store/newLearningRequest";
import { isStageTerminal } from "utils/helpers";
import { CommissioningDetails } from "./steps/new-learning-details/commissioning-details/commissioning-details";
import { RequestSubmitted } from "./steps/new-learning-details/request-submitted/request-submitted";

interface NewLearningSurveyDialogProps {
  open: boolean;
  handleClose: () => void;
  onSubmitSuccess?: () => void;
}

export const NewLearningSurveyDialog = ({
  open,
  handleClose,
  onSubmitSuccess,
}: NewLearningSurveyDialogProps): React.ReactElement => {
  const { isMobile } = useViewport();
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useDialogStyles({ isMobile });
  const [courseSelection, setCourseSelection] = useState<boolean | null>(null);
  const [dialogTitle, setDialogTitle] = useState<string>("");
  // Refs so the cleanup closure always reads the latest values without stale captures
  const hasAcceptedRecommendationRef = useRef(false);
  const { cancel } = useCancelLearningRequest();
  const dispatch = useAppDispatch();
  const workflowStage = useSelector((state: RootState) => state.newLearningRequest.workflowStage);
  const workflowStageRef = useRef(workflowStage);
  useEffect(() => {
    workflowStageRef.current = workflowStage;
  }, [workflowStage]);

  const [currentStep, setCurrentStep] = useState(0);
  const userContext = useSelector((state: RootState) => state.core.userContext);

  // When step changes, we move focus to content area so screen reader users know context changed
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      workflowStageRef.current = null;
      dispatch(setWorkflowStage(null));
    }

    if (!open) {
      setCurrentStep(0);
      hasAcceptedRecommendationRef.current = false;
      setDialogTitle("");
    }
  }, [open, dispatch]);

  // Focus management: When step changes, focus the content area
  // This announces the new content to screen readers and provides context
  useEffect(() => {
    if (open && contentRef.current) {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          // make div focusable and move focus for screen readers
          contentRef.current.tabIndex = -1;
          contentRef.current.focus();
        }
      });
    }
  }, [currentStep, open]);

  const handleNext = (currentStepParam: NewLearningRequestStep) => {
    setCurrentStep(currentStepParam);
  };

  const handlePrevious = (currentStepParam: NewLearningRequestStep) => {
    setCurrentStep(currentStepParam);
  };

  const handleStepSuccess = useCallback(() => {
    onSubmitSuccess?.();
    handleClose();
  }, [onSubmitSuccess, handleClose]);

  const renderStepContent = () => {
    switch (currentStep) {
      case NewLearningRequestStep.NewLearning:
        return (
          <NewLearning
            onNext={handleNext}
            isMobile={isMobile}
            courseSelection={courseSelection}
            setCourseSelection={setCourseSelection}
            setDialogTitle={setDialogTitle}
          />
        );

      case NewLearningRequestStep.LearningAnalysis:
        return (
          <LearningAnalysis
            onPrevious={handlePrevious}
            isMobile={isMobile}
            onNext={handleNext}
            setDialogTitle={setDialogTitle}
          />
        );

      case NewLearningRequestStep.CourseDetails:
        return (
          <CourseDetails isMobile={isMobile} onNext={handleNext} setDialogTitle={setDialogTitle} />
        );

      case NewLearningRequestStep.BudgetCheck:
        return (
          <BudgetCheck
            isMobile={isMobile}
            onNext={handleNext}
            onClose={handleStepSuccess}
            setDialogTitle={setDialogTitle}
          />
        );

      case NewLearningRequestStep.CommissioningDetails:
        return (
          <CommissioningDetails
            isMobile={isMobile}
            onNext={handleNext}
            userContext={userContext}
            setDialogTitle={setDialogTitle}
          />
        );

      case NewLearningRequestStep.RequestSubmitted:
        return (
          <RequestSubmitted
            isMobile={isMobile}
            onClose={handleStepSuccess}
            setDialogTitle={setDialogTitle}
          />
        );

      case NewLearningRequestStep.Recommendations:
        return (
          <ProductRecommendations
            onPrevious={handlePrevious}
            isMobile={isMobile}
            onAcceptRecommendation={() => {
              hasAcceptedRecommendationRef.current = true;
            }}
            closeDialog={handleClose}
            setDialogTitle={setDialogTitle}
          />
        );

      default:
        return null;
    }
  };

  const cancelRequest = useCallback(async () => {
    const cancelled = await cancel();
    if (cancelled) {
      dispatch(setWorkflowStage(WorkflowStageEnum.Cancelled));
    }
  }, [cancel, dispatch]);

  useEffect(() => {
    if (!open) return;
    return () => {
      const hasTerminalLifecycle =
        hasAcceptedRecommendationRef.current || isStageTerminal(workflowStageRef.current);

      if (!hasTerminalLifecycle) {
        cancelRequest();
      }
    };
  }, [open, cancelRequest]);

  return (
    <Dialog
      id="new_learning_survey_dialog"
      onClose={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="title"
      open={open}
      minWidth={isSmallScreen ? 0 : 900}
      maxHeight={"100vh"}
      fullScreen={isSmallScreen}
      showCloseIcon={false}
      title={dialogTitle}
    >
      <Box className={classes.gradientHeader}>
        <Box className={classes.sparkleFloat1}>
          <SparklesIcon size={24} />
        </Box>
        <Box className={classes.sparkleFloat2}>
          <SparklesIcon size={16} />
        </Box>

        <Box className={classes.headerContent}>
          <Box className={classes.headerTitle}>
            <Box className={classes.iconBox}>
              <SparklesIcon size={24} />
            </Box>
            <h2 className={classes.titleText} id="title">
              {dialogTitle}
            </h2>
          </Box>
          <IconButton
            onClick={handleClose}
            className={classes.closeButton}
            size="small"
            aria-label={t("close_survey")}
          >
            <CloseIcon size={20} />
          </IconButton>
        </Box>
      </Box>

      <div ref={contentRef} style={{ outline: "none" }}>
        <Box className={classes.contentContainer}>{renderStepContent()}</Box>
      </div>
    </Dialog>
  );
};
